import {
    Action,
    ANY_ROLE,
    AppFeature,
    ReadablePermissions,
    RoleRule,
    UserPermissionsConfig,
    UserRoles,
} from 'src/stores/UserStore/UserStore.types';

/**
 * Permission matrix — THE single source of truth for who can do what.
 *
 * `view`   — access to a section (route, menu item).
 * `manage` — mutating UI actions (export / import / create buttons).
 *
 * A user `can.<action>.<feature>` when the intersection of their roles with
 * `userPermissionsConfig[feature][action]` is non-empty, OR the list contains
 * the `*` wildcard (any authenticated user). An action left out for a feature
 * is therefore always `false`.
 *
 * Role model (only three roles exist):
 *   - CreditOfficer    — everything, incl. loan-application workflow actions
 *                        (assign / reassign / decline / mark-as-fully-paid).
 *   - FinancialOfficer — the standalone transactions module ONLY (list +
 *                        import + active-applications export) — nothing else.
 *                        No loan-application access.
 *   - SalesOfficer     — loan applications only. Per-application
 *                        transactions live INSIDE the loan-application details
 *                        page (the `loanApplications` feature), NOT the
 *                        standalone transactions module — so SalesOfficer has no
 *                        `transactions` access here. No workflow actions.
 *
 * For `loanApplications`, `view` covers everything Credit + Sales share —
 * the list, details, merchant snapshots, the per-application transactions
 * (repayments) and comments tabs, including adding a comment (the one write
 * action Sales shares). `manage` is reserved for the mutating workflow buttons
 * and the reassignment reasons tab, which are CreditOfficer-only.
 *
 * The active-applications export button lives in the standalone transactions
 * module (`TransactionsHeaderPanel`) and is gated by `manage.transactions`, so
 * both CreditOfficer and FinancialOfficer can download applications.
 */
export const userPermissionsConfig: UserPermissionsConfig = {
    // CreditOfficer has everything; partners/merchants are admin-only areas.
    partners: {
        view: [UserRoles.CreditOfficer],
        manage: [UserRoles.CreditOfficer],
    },
    merchants: {
        view: [UserRoles.CreditOfficer],
        manage: [UserRoles.CreditOfficer],
    },
    // view: list + details + merchant snapshots + per-application transactions
    // (repayments) + comments (incl. adding a comment) — CreditOfficer +
    // SalesOfficer.
    // FinancialOfficer has NO loan-application access (transactions module only).
    // manage: mutating workflow buttons (assign / reassign / decline /
    // mark-as-fully-paid) + the reassignment reasons tab — CreditOfficer only.
    loanApplications: {
        view: [UserRoles.CreditOfficer, UserRoles.SalesOfficer],
        manage: [UserRoles.CreditOfficer],
    },
    // Agent portal loan applications — role-agnostic (usable by any user).
    agentLoanApplications: {
        view: [ANY_ROLE],
        manage: [ANY_ROLE],
    },
    // Standalone transactions module (list + import + active-applications
    // export) — not for SalesOfficer.
    transactions: {
        view: [UserRoles.CreditOfficer, UserRoles.FinancialOfficer],
        manage: [UserRoles.CreditOfficer, UserRoles.FinancialOfficer],
    },
};

const ACTIONS: Action[] = ['manage', 'view'];

const hasIntersection = (
    allowedRoles: RoleRule[] | undefined,
    userRoles: string[],
): boolean => {
    if (!allowedRoles) {
        return false;
    }

    if (allowedRoles.includes(ANY_ROLE)) {
        return true;
    }

    return allowedRoles.some((role) => userRoles.includes(role));
};

/**
 * Intersects the user's roles with the matrix and returns the nested boolean
 * map. `positive === false` yields the negation (used to build `canNot`).
 */
export const generateReadablePermissions = (
    userRoles: string[],
    positive = true,
): ReadablePermissions => {
    const result = {} as ReadablePermissions;

    ACTIONS.forEach((action) => {
        result[action] = {} as Record<AppFeature, boolean>;

        (Object.keys(userPermissionsConfig) as AppFeature[]).forEach(
            (feature) => {
                const can = hasIntersection(
                    userPermissionsConfig[feature][action],
                    userRoles,
                );

                result[action][feature] = positive ? can : !can;
            },
        );
    });

    return result;
};
